import { getMutationSelector, MutationState } from '@redux-requests/core';
import { createSelector } from '@reduxjs/toolkit';
import { PDFManagerFactory } from 'pdftron';
import {
  DocumentData,
  DocumentTextProcessing,
  DocumentTypes,
  InputDocuments,
  InputPage,
  ManualPageRotation,
  InspectionState,
  InspectionStatuses,
  PDFTronTools,
  PDFTronToolsDBVals,
  StoreState,
  InputAnnotation,
  Crop,
  CTFModuleTypes,
  Difference,
  SerializedManualAlignZone,
} from 'types';
import { excludedRange } from 'utils/pageRanges/pageRangeConversion';
import { updateDifferenceViewOptions, CreateInspectionData } from 'store/request/inspections/actions';
import { getFilesIds } from '../files/filesSelectors';
import { CtfMatchResultWithDifferences } from 'store/request/match/types';
import {
  filterDifferencesByModule,
  getDisplayedDifferenceGroups,
  getDifferenceSortOption,
} from 'store/request/inspections/selectors';
import { getSelectedModule } from 'store/app/appSelectors';
import { getDifferences } from 'store/request/differences/selectors';
import { getGroups } from 'store/request/difference-group/selectors';

import { getShowRepeats, getHideDisplayPanels } from 'store/app/appSelectors';
import GraphicZone from 'pdftron/docManager/AnnotationTools/GraphicZone';
import { InspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import { ColorId, Dictionaries, GraphicsSensitivity, MatchingMode } from '@verify/shared-utils';

// Step-and-repeat list-item types/enum and the pure list builder live in a pdftron-free module
// so the builder can be unit-tested. Imported for internal use and re-exported so existing
// `store` barrel imports keep working.
import { ListItemType, buildStepAndRepeatListItems, findRepeatScrollIndex } from './stepAndRepeatListItems';
import type { ListItem, RepeatWithPage } from './stepAndRepeatListItems';

export { ListItemType, buildStepAndRepeatListItems, findRepeatScrollIndex };
export type { ListItem, RepeatWithPage };

const getInspectionstate = (state: StoreState) => state.inspection;

const getSourcePageRange = (state: StoreState) => state.inspection.source.pageRange;
const getTargetPageRange = (state: StoreState) => state.inspection.target.pageRange;

const getSourceTotalPageCount = (state: StoreState) => state.inspection.source.totalPageCount;
const getTargetTotalPageCount = (state: StoreState) => state.inspection.target.totalPageCount;

const getSourceLoaded = (state: StoreState) => state.inspection.source.state === 'LOADED';
const getTargetLoaded = (state: StoreState) => state.inspection.target.state === 'LOADED';
const getReportLoaded = (state: StoreState) => state.inspection.report.state === 'LOADED';

const getSourceLiveText = (state: StoreState) => state.inspection.source.liveText;
const getTargetLiveText = (state: StoreState) => state.inspection.target.liveText;

const getInternalAnnotationVisibility = (state: StoreState) => state.inspection.internalAnnotationsVisibility;
const getInternalAnnotationsVisibilityDefault = (state: StoreState) =>
  state.inspection.internalAnnotationsVisibilityDefault;

const getShowLibrarySource = (state: StoreState) => state.inspection.showLibrary.source;
const getShowLibraryTarget = (state: StoreState) => state.inspection.showLibrary.target;

const getSourceTextProcess = (state: StoreState) =>
  state.inspection.source.documentTextProcessing === DocumentTextProcessing.COMPLETED;
const getTargetTextProcess = (state: StoreState) =>
  state.inspection.target.documentTextProcessing === DocumentTextProcessing.COMPLETED;

const getName = (state: StoreState) => state.inspection.name;

const getInspectionId = (state: StoreState) => state.inspection.inspectionId;

const getSourceTotalPage = (state: StoreState) => state.inspection.source.totalPageCount;
const getTargetTotalPage = (state: StoreState) => state.inspection.target.totalPageCount;

const getManualSelectedZoneId = (state: StoreState) => state.inspection.manualSelectedZoneId;

const getManualSelectedAnnotationId = (state: StoreState) => state.inspection.manualSelectedAnnotationId;

const getManualAlignMode = (state: StoreState) => state.inspection.manualAlignMode;

const getSourceLayers = (state: StoreState) => state.inspection.source.layers;
const getTargetLayers = (state: StoreState) => state.inspection.target.layers;

const getSourceOverprint = (state: StoreState) => state.inspection.source.overprint ?? true;
const getTargetOverprint = (state: StoreState) => state.inspection.target.overprint ?? true;

const getSourceSeparations = (state: StoreState) => state.inspection.source.separations;
const getTargetSeparations = (state: StoreState) => state.inspection.target.separations;

const getSeparationHidden = createSelector([getSourceSeparations, getTargetSeparations], (source, target) => {
  const allSeparations = [...(source || []), ...(target || [])];
  return allSeparations.some((separation) => !separation.enabled);
});

/** The only fields of a document that page data is derived from. */
type PagesDataSource = Pick<DocumentData, 'pageRange' | 'totalPageCount' | 'annotations'>;

function getPagesData(documentData: PagesDataSource): InputPage[] {
  const pages: InputPage[] = [];
  const pageNumbers = documentData.pageRange.filter((x) => x <= documentData.totalPageCount); // Make sure we don't include pages that doesn't exist
  for (let i = 1; i <= documentData.totalPageCount; i++) {
    const page = {
      number: i,
      included: pageNumbers.length === 0 || pageNumbers.includes(i), // Either all pages are included or a specified subset
    };
    pages.push(page);
  }

  if (documentData.annotations && Array.isArray(documentData.annotations)) {
    documentData.annotations.forEach((annotation) => {
      const page = pages[annotation.page - 1];
      // Make sure we don't include annotations on excluded pages
      if (!page?.included) {
        return;
      }
      switch (annotation.usedTool) {
        case PDFTronTools.CROSSOUT:
          if (!page.crossout) {
            page.crossout = [];
          }
          if (annotation.quads) {
            page.crossout.push({
              number: annotation.number || 0,
              quads: annotation.quads,
            });
          }
          break;
        case PDFTronTools.CROP:
          if (!page.crop) {
            page.crop = [];
          }
          if (annotation.cropZone) {
            page.crop.push({ ...annotation.cropZone });
          }
          break;
        case PDFTronTools.POLYGON:
          if (!page.crop) {
            page.crop = [];
          }
          if (annotation.cropZone) {
            page.crop.push({ ...annotation.cropZone });
          }
          break;
        default:
      }
    });
  }
  return pages;
}

function addMarqueeZones(state: InspectionState, sourcePages: InputPage[], targetPages: InputPage[]) {
  const sourceData = state[DocumentTypes.source];
  const targetData = state[DocumentTypes.target];

  sourceData.annotations.forEach((sourceAnnotation) => {
    if (sourceAnnotation && sourceAnnotation.quads && sourceAnnotation.usedTool === PDFTronTools.MARQUEE_ZONE) {
      const sourcePage = sourcePages[sourceAnnotation.page - 1];
      if (!sourcePage) {
        return;
      }
      if (!sourcePage.zone) {
        sourcePage.zone = [];
      }
      const zoneNumber = sourceAnnotation.number;
      if (!zoneNumber) {
        return;
      }

      sourcePage.zone.push({
        number: zoneNumber,
        quads: sourceAnnotation.quads,
        usedTool: PDFTronToolsDBVals.MARQUEE_ZONE,
        annotationId: sourceAnnotation.annotationId,
      });
    }
  });
  targetData.annotations.forEach((targetAnnotation) => {
    if (targetAnnotation && targetAnnotation.quads && targetAnnotation.usedTool === PDFTronTools.MARQUEE_ZONE) {
      const targetPage = targetPages[targetAnnotation.page - 1];
      if (!targetPage) {
        return;
      }
      if (!targetPage.zone) {
        targetPage.zone = [];
      }
      const zoneNumber = targetAnnotation.number;
      if (!zoneNumber) {
        return;
      }

      targetPage.zone.push({
        number: zoneNumber,
        quads: targetAnnotation.quads,
        usedTool: PDFTronToolsDBVals.MARQUEE_ZONE,
        annotationId: targetAnnotation.annotationId,
      });
    }
  });
}

function addZonesData(state: InspectionState, sourcePages: InputPage[], targetPages: InputPage[]) {
  const sourceData = state[DocumentTypes.source];
  const targetData = state[DocumentTypes.target];
  if (
    sourceData.zoneSelectedTexts &&
    Array.isArray(sourceData.zoneSelectedTexts) &&
    sourceData.annotations &&
    Array.isArray(sourceData.annotations) &&
    targetData.annotations &&
    Array.isArray(targetData.annotations) &&
    sourcePages.length &&
    targetPages.length
  ) {
    sourceData.zoneSelectedTexts.forEach((zoneText) => {
      const sourceAnnotation = sourceData.annotations.find((annot) => annot.annotationId === zoneText.annotationId);
      if (!sourceAnnotation || !sourceAnnotation.quads) {
        return; // We couldn't find the related annotation
      }
      const targetAnnotations = targetData.annotations.filter(
        (annot) => annot.number === sourceAnnotation.number && annot.usedTool === PDFTronTools.ZONE,
      );
      if (!targetAnnotations || !targetAnnotations?.[0]?.quads) {
        return; // We couldn't find the related annotation on target document
      }
      const sourcePage = sourcePages[sourceAnnotation.page - 1];
      const targetPage = targetPages[targetAnnotations[0].page - 1];
      // Make sure we don't include annotations on excluded pages
      if (!sourcePage.included || !targetPage.included) {
        return;
      }
      const zoneNumber = sourceAnnotation.number;
      if (!zoneNumber) {
        return;
      }
      // Add zone markup on source
      if (!sourcePage.zone) {
        sourcePage.zone = [];
      }
      sourcePage.zone.push({
        number: zoneNumber,
        quads: sourceAnnotation.quads,
        usedTool: PDFTronToolsDBVals.ZONE,
        annotationId: sourceAnnotation.annotationId,
        groupId: sourceAnnotation.groupId,
      });
      // Add zone markup on target
      if (!targetPage.zone) {
        targetPage.zone = [];
      }

      targetAnnotations.forEach((targetAnnotation) => {
        // Check if this annotation is already in the zone
        const annotationExists = targetPage.zone?.some(
          (zoneItem) => zoneItem.annotationId === targetAnnotation.annotationId,
        );
        if (!annotationExists) {
          targetPage.zone?.push({
            number: zoneNumber,
            quads: targetAnnotation.quads || [],
            usedTool: PDFTronToolsDBVals.ZONE,
            annotationId: targetAnnotation.annotationId,
            groupId: targetAnnotation.groupId,
          });
        }
      });
    });
  }
}

function getManualAlignZonesData(documentData: Pick<DocumentData, 'annotations'>): SerializedManualAlignZone[] {
  if (!documentData.annotations || !Array.isArray(documentData.annotations)) return [];
  return documentData.annotations
    .filter((annotation) => annotation.manualAlignZone)
    .map((annotation) => annotation.manualAlignZone!)
    .sort((a, b) => (a.linkedZoneId || a.annotationId).localeCompare(b.linkedZoneId || b.annotationId));
}

type PageRotationReader = {
  getPageRotations: () => Record<number, number>;
};

export function getManualPageRotations(documentViewer?: PageRotationReader): ManualPageRotation[] | undefined {
  if (!documentViewer) return undefined;

  const rotations = Object.entries(documentViewer.getPageRotations())
    .map(([pageNumber, rotation]) => ({
      angle: rotation * 90,
      pageNumber: Number(pageNumber),
    }))
    .filter(({ angle }) => angle !== 0);

  return rotations.length > 0 ? rotations : undefined;
}

const getSelectingZone = createSelector([getInspectionstate], (inspection) => inspection.selectingZone || false);

const getActivePolygonCreation = createSelector([getInspectionstate], (inspection) => inspection.activePolygonCreation);

const getInspectionInput = (state: StoreState): InputDocuments => {
  const sourcePages = getPagesData(state.inspection[DocumentTypes.source]);
  const targetPages = getPagesData(state.inspection[DocumentTypes.target]);
  addZonesData(state.inspection, sourcePages, targetPages);
  addMarqueeZones(state.inspection, sourcePages, targetPages);
  const pdfDocManager = PDFManagerFactory.getPDFDocManager();
  const sourceGraphicZones = pdfDocManager?.getSerializedGraphicZones();
  const targetGraphicZones = pdfDocManager?.getSerializedGraphicZones(DocumentTypes.target);
  const sourcePolygons = getDocumentPolygons(state, DocumentTypes.source);
  const targetPolygons = getDocumentPolygons(state, DocumentTypes.target);

  const sourceLayers = getSourceLayers(state);
  const targetLayers = getTargetLayers(state);
  const sourceSeparations = getSourceSeparations(state);
  const targetSeparations = getTargetSeparations(state);
  const sourceManualAlignZones = getManualAlignZonesData(state.inspection[DocumentTypes.source]);
  const targetManualAlignZones = getManualAlignZonesData(state.inspection[DocumentTypes.target]);
  const sourceManualPageRotations = getManualPageRotations(
    pdfDocManager?.getInstance(DocumentTypes.source)?.instance.Core.documentViewer,
  );
  const targetManualPageRotations = getManualPageRotations(
    pdfDocManager?.getInstance(DocumentTypes.target)?.instance.Core.documentViewer,
  );
  const samplingPoints = pdfDocManager?.getSerializedSamplingPoints();

  const input: InputDocuments = {
    documents: [
      {
        pages: sourcePages,
        graphicZones: sourceGraphicZones,
        manualAlignZones: sourceManualAlignZones.length > 0 ? sourceManualAlignZones : undefined,
        manualPageRotations: sourceManualPageRotations,
        layers: sourceLayers,
        separations: sourceSeparations,
        type: 'master',
        polygons: sourcePolygons,
        overprint: state.inspection[DocumentTypes.source]?.overprint,
      },
      {
        pages: targetPages,
        graphicZones: targetGraphicZones,
        manualAlignZones: targetManualAlignZones.length > 0 ? targetManualAlignZones : undefined,
        manualPageRotations: targetManualPageRotations,
        layers: targetLayers,
        separations: targetSeparations,
        type: 'sample',
        polygons: targetPolygons,
        overprint: state.inspection[DocumentTypes.target]?.overprint,
        samplingPoints: samplingPoints && samplingPoints.length > 0 ? samplingPoints : undefined,
      },
    ],
  };
  return input;
};
const getInspectionStatus = (state: StoreState) => state.inspection.inspectionStatus;

const getLoadingDifferences = (state: StoreState) => state.inspection.reloadingDifferences;

const getResyncLoading = (state: StoreState) => state.inspection.resyncStatus.loading;
const getProcessingResyncResults = (state: StoreState) => state.inspection.resyncStatus.processingResults;
const getUndoingResync = (state: StoreState) => state.inspection.resyncStatus.undoingResync;

const getSearchingForGraphic = (state: StoreState) => state.inspection.searchingForGraphicZone;

const getZoneSelectedTexts = (state: StoreState) => state.inspection.source.zoneSelectedTexts;

const getSelectedTool = (state: StoreState) => state.inspection.selectedTool;

const getSourceAnnotations = (state: StoreState) => state.inspection.source.annotations;
const getTargetAnnotations = (state: StoreState) => state.inspection.target.annotations;

const getSourceCurrentPage = (state: StoreState) => state.inspection.source.currentPage;
const getTargetCurrentPage = (state: StoreState) => state.inspection.target.currentPage;

const getSourcePages = (state: StoreState) => state.inspection.source.pages;
const getTargetPages = (state: StoreState) => state.inspection.target.pages;

const getSyncScrolling = (state: StoreState) => state.inspection.syncScrolling;

const getDifferencePanelOpen = (state: StoreState) => state.inspection.differencePanelOpen;

const getChangeGridDirection = (state: StoreState) => state.inspection.changeGridDirection;

const getChangeGridWidth = (state: StoreState) => state.inspection.changeGridWidth;

const getInvalidScaledGraphicZoneRatio = (state: StoreState) => state.inspection.invalidScaledGraphicZoneRatio;
const getUnconfirmTextZoneError = (state: StoreState) => state.inspection.unconfirmTextZoneError;
const getUnconfirmGraphicZoneError = (state: StoreState) => state.inspection.unconfirmedGraphicZoneError;
const getUnconfirmedMarqueeZoneError = (state: StoreState) => state.inspection.unconfirmedMarqueeZoneError;
const getChangeToolError = (state: StoreState) => state.inspection.changeToolError;
const getPendingCropZone = (state: StoreState) => state.inspection.pendingCropZone;
const getPendingShapesRequest = (state: StoreState) => state.inspection.pendingShapeRequest;
const getNoResultAnnotationId = (state: StoreState) => state.inspection.noResultAnnotationId;

const getPersistedDifferenceComments = (state: StoreState) => state.inspection.persistedDifferenceComments;

const getSourceState = (state: StoreState) => state.inspection.source.state;
const getTargetState = (state: StoreState) => state.inspection.target.state;
const getReportState = (state: StoreState) => state.inspection.report.state;

const getInspectionLastJobId = (state: StoreState) => state.inspection.lastJobId;

const getLastJobCreatedAt = (state: StoreState) => state.inspection.lastJobCreatedAt;

const getLastJobInput = (state: StoreState) => state.inspection.lastJobInput;

const getOpenPanel = (state: StoreState) => state.inspection.openPanel;

const getPageRangeOption = (state: StoreState) => state.inspection.pageRangeOption;

const getNextTextZoneId = (state: StoreState) => state.inspection.nextTextZoneId;
const getNextGraphicZoneId = (state: StoreState) => state.inspection.nextGraphicZoneId;
const getNextCropZoneId = (state: StoreState) => state.inspection.nextCropZoneId;
const getNextMarqueeZoneId = (state: StoreState) => state.inspection.nextMarqueeZoneId;
const getNextCrossoutZoneId = (state: StoreState) => state.inspection.nextCrossoutZoneId;
const getNextColorPointId = (state: StoreState) => state.inspection.nextColorPointId;
const getNextManualAlignZoneId = (state: StoreState) => state.inspection.nextManualAlignZoneId;

const getHighlightColor = (state: StoreState): ColorId =>
  state.inspection.settings.highlightColor || ColorId.MULTICOLOR;

const getLastJobMatches = (state: StoreState) => state.inspection.lastJobMatches;
const getLastJobImages = (state: StoreState) => state.inspection.lastJobImages;

const getImagesPath = (document: 'master' | 'sample', basePathKey: 'pagesBasePath' | 'differenceThumbnailBasePath') =>
  createSelector([getLastJobImages], (lastJobImages) => {
    const isLocalhost = window.location.hostname === 'localhost';
    const isAiDomain = window.location.hostname.endsWith('.ai');
    if (lastJobImages && Object.keys(lastJobImages).length > 0 && lastJobImages[document]) {
      const path = lastJobImages[document][basePathKey];
      const cdnUrl = isAiDomain ? import.meta.env.VITE_VERIFY_CDN_AI_URL : import.meta.env.VITE_VERIFY_CDN_URL;
      const url = `${cdnUrl}/${path}`;
      return isLocalhost ? `/cdn/${path}` : url;
    }
    return '';
  });

const getImagesPathPrefix = () => {
  const isLocalhost = window.location.hostname === 'localhost';
  const isAiDomain = window.location.hostname.endsWith('.ai');
  const cdnUrl = isAiDomain ? import.meta.env.VITE_VERIFY_CDN_AI_URL : import.meta.env.VITE_VERIFY_CDN_URL;
  const url = `${cdnUrl}/`;
  return isLocalhost ? `/cdn/` : url;
};

const getPagesImagesPath = (document: 'master' | 'sample') => getImagesPath(document, 'pagesBasePath');

const getThumbnailsImagesPath = (document: 'master' | 'sample') =>
  getImagesPath(document, 'differenceThumbnailBasePath');

const getMatchDetailsByMatchIndex = (matchIndex: number) =>
  createSelector([getLastJobMatches], (matches) => {
    return matches.find((match) => match.index === matchIndex)?.matchDetails[0];
  });

const filterAnnotationsByTools = (annotations: InputAnnotation[], tools: PDFTronTools[]) => {
  const filteredAnnotations = [];
  for (let i = 0; i < annotations.length; i++) {
    if (tools.includes(annotations[i].usedTool as PDFTronTools)) {
      filteredAnnotations.push(annotations[i]);
    }
  }
  return filteredAnnotations;
};

// check if the inspection used graphic zones but now has crop tool enabled.
const getInspectionHasOutdatedGraphicZones = createSelector([getLastJobInput], (lastJob) => {
  return !!(
    lastJob &&
    lastJob.documents &&
    lastJob.documents.some((doc) => doc.graphicZones && doc.graphicZones.length > 0)
  );
});

const getIsFilesLoaded = (state: StoreState) => state.inspection.isFilesLoaded;

const getCtfJobs = (state: StoreState) => state.inspection.ctfJobs;

const getTextMatchJobId = (state: StoreState) => state.inspection.textMatchJobId;

const getTextMatchCdnPathPrefix = (state: StoreState) => state.inspection.textMatchCdnPathPrefix;

const getTextMatchMetadata = (state: StoreState) => state.inspection.textMatchMetadata;

const getInspectionSettings = createSelector([getInspectionstate], (inspectionState) => inspectionState.settings);

const getLastJobMetadata = (state: StoreState) => state.inspection.lastJobMetadata;

const getLastJobCtfVersion = (state: StoreState) => state.inspection.lastJobCtfVersion;

const getIsSingleFile = createSelector([getInspectionstate], (inspectionState) => {
  return inspectionState.settings.singleFile;
});

const getIsStepAndRepeat = createSelector([getLastJobMatches], (lastJobMatches) => {
  if (!lastJobMatches || lastJobMatches.length === 0) return false;
  return lastJobMatches.some((match) => match.rowNumber > 1 || match.columnNumber > 1);
});

const getRepeats = createSelector(
  [getIsStepAndRepeat, getLastJobMatches, getDifferences],
  (isStepAndRepeat, lastJobMatches, differences) => {
    if (!isStepAndRepeat || !lastJobMatches) return [];

    const repeats: CtfMatchResultWithDifferences[] = lastJobMatches.map((match) => ({
      ...match,
      differences: [],
    }));

    if (!differences || differences.length === 0) return repeats;

    const graphicsDifferences = filterDifferencesByModule(differences, CTFModuleTypes.graphics);

    if (!graphicsDifferences) return repeats;

    graphicsDifferences.forEach((difference) => {
      const { matchIndex: differenceMatchIndex } = difference;

      if (differenceMatchIndex !== undefined) {
        const repeat = repeats.find((repeat) => repeat.index === differenceMatchIndex);
        if (repeat) {
          repeat.differences.push(difference);
        }
      }
    });

    return repeats;
  },
);

const getDisplayedRepeats = createSelector(
  [getRepeats, getSelectedModule, getShowRepeats],
  (repeats, selectedModule, showRepeats) => {
    if (selectedModule !== CTFModuleTypes.graphics || !showRepeats) {
      return [];
    }

    const animatedZone = GraphicZone.animatedZone === '' ? null : Number(GraphicZone.animatedZone); // animatedZone will be the index of the match that is currently flashing
    const displayedRepeats = repeats.filter((repeat) => repeat.index !== animatedZone); // we remove the match that is flashing from displayedRepeats so it isn't drawn beneath the flashing annotations
    return displayedRepeats;
  },
);

const getDisplayedAndHiddenRepeats = createSelector(
  [getRepeats, getSelectedModule, getShowRepeats, getDifferences, getGroups, getInspectionstate],
  (repeats, selectedModule, showRepeats, differences, groups, inspectionState) => {
    // Only process repeats in graphics module
    if (selectedModule !== CTFModuleTypes.graphics) {
      return { displayed: [], hidden: repeats };
    }

    const animatedZone = GraphicZone.animatedZone === '' ? null : Number(GraphicZone.animatedZone);

    let displayedRepeats: CtfMatchResultWithDifferences[];
    let hiddenRepeats: CtfMatchResultWithDifferences[];

    if (showRepeats) {
      // Show all repeats except animated zone
      displayedRepeats = repeats.filter((repeat: CtfMatchResultWithDifferences) => repeat.index !== animatedZone);
      hiddenRepeats = repeats.filter((repeat: CtfMatchResultWithDifferences) => repeat.index === animatedZone);
    } else {
      // Hide all repeats
      displayedRepeats = [];
      hiddenRepeats = repeats;
    }

    return {
      displayed: displayedRepeats,
      hidden: hiddenRepeats,
    };
  },
);

const getRepeatsPerPage = createSelector(
  [getRepeats, getDisplayedDifferenceGroups],
  (repeats, displayedDifferenceGroups) => {
    const repeatsPerPage: Record<
      // Source page number
      number,
      // we need to keep the number of diffs detected during inspection separate from the number of diffs post filtering.
      // some UI will change depending on whether a repeat has 0 differences as a result of filtering or not existing in the first place.
      (CtfMatchResultWithDifferences & { differencesPostFiltering: Difference[] })[]
    > = {};

    // Add repeats to their respective pages
    repeats?.forEach((repeat) => {
      const { pageNumber: sourcePageNumber } = repeat.inputMasterLocation;

      if (!repeatsPerPage[sourcePageNumber]) {
        repeatsPerPage[sourcePageNumber] = [];
      }

      repeatsPerPage[sourcePageNumber].push({
        ...repeat,
        differencesPostFiltering: [],
      });
    });

    if (displayedDifferenceGroups) {
      const displayedGraphicsDifferences = filterDifferencesByModule(
        displayedDifferenceGroups,
        CTFModuleTypes.graphics,
      );

      if (!displayedGraphicsDifferences) return repeatsPerPage;

      displayedGraphicsDifferences.forEach((difference) => {
        const { matchIndex: differenceMatchIndex } = difference;
        const sourcePageNumber = difference.source.location.pageNumber;

        if (differenceMatchIndex !== undefined && repeatsPerPage[sourcePageNumber]) {
          const repeat = repeatsPerPage[sourcePageNumber].find((repeat) => repeat.index === differenceMatchIndex);
          if (repeat) {
            repeat.differencesPostFiltering.push(difference);
          }
        }
      });
    }

    return repeatsPerPage;
  },
);

const getRepeatHasDifferences = (repeatIndex: number) =>
  createSelector([getRepeats], (repeats) => {
    const repeat = repeats.find((repeat) => repeat.index === repeatIndex);
    if (!repeat || !repeat.differences) {
      return false;
    }
    return repeat.differences.length > 0;
  });

const getRepeatIsDisplayed = (repeatIndex: number) =>
  createSelector([getDisplayedRepeats], (displayedRepeats) => {
    return displayedRepeats.some((repeat) => repeat.index === repeatIndex);
  });

const getSelectedCustomDictionaryIDs = createSelector(
  [getInspectionstate],
  (inspectionState) => inspectionState.customDictionaryIDs,
);

const getAutoMatchGraphic = createSelector([getInspectionstate], (inspectionState) => inspectionState.autoMatchGraphic);

const getTableEnhancementEnabled = createSelector(
  [getInspectionstate],
  (inspectionState) => inspectionState.tableEnhancementEnabledState,
);

const getShowLibrary = createSelector([getShowLibrarySource, getShowLibraryTarget], (source, target) => ({
  source,
  target,
}));

const getPageRangeSelector = createSelector([getSourcePageRange, getTargetPageRange], (source, target) => ({
  source,
  target,
}));

const getExcludedPage = createSelector(
  [getSourcePageRange, getSourceTotalPageCount, getTargetPageRange, getTargetTotalPageCount],
  (sourcePageRange, sourceTotalPageCount, targetPageRange, targetTotalPageCount) => ({
    source: excludedRange(sourcePageRange, sourceTotalPageCount),
    target: excludedRange(targetPageRange, targetTotalPageCount),
  }),
);

const getPageLoadedSelector = createSelector([getSourceLoaded, getTargetLoaded], (source, target) => ({
  source,
  target,
}));

// Check if the Live text process is completed and the the live text is not existing
const getLiveTextNotFound = createSelector(
  [getSourceTextProcess, getTargetTextProcess, getSourceLiveText, getTargetLiveText],
  (sourceProcess, targetProcess, source, target) => (sourceProcess && !source) || (targetProcess && !target),
);

const getEitherLiveText = createSelector(
  [getSourceTextProcess, getTargetTextProcess, getSourceLiveText, getTargetLiveText],
  (sourceProcess, targetProcess, source, target) => (sourceProcess && source) || (targetProcess && target),
);

const getLiveTextStatus = createSelector(
  [getSourceTextProcess, getTargetTextProcess, getSourceLiveText, getTargetLiveText],
  (sourceProcess, targetProcess, source, target) => ({
    source: sourceProcess && !source,
    target: targetProcess && !target,
  }),
);

// Check if the document is loaded and the Live text process is completed
const getTextProcessStatus = createSelector(
  [getSourceTextProcess, getTargetTextProcess, getInspectionSettings],
  (sourceProcess, targetProcess, inspectionSettings) => {
    if (inspectionSettings.singleFile) {
      return targetProcess;
    }
    return sourceProcess && targetProcess;
  },
);

// TODO: totalPages should be taken from webViewerInstance. It's not changing and there is not point in storing it redux.
const getTotalPageCount = createSelector([getSourceTotalPage, getTargetTotalPage], (source, target) => ({
  source,
  target,
}));

const getDocumentDisplayedSelector = createSelector(
  [getSourceLoaded, getTargetLoaded, getSourceTotalPage, getTargetTotalPage, getInspectionSettings],
  (sourceLoaded, targetLoaded, sourceTotalPages, targetTotalPages, inspectionSettings) => {
    if (inspectionSettings.singleFile) {
      return targetLoaded && targetTotalPages > 0;
    }
    return sourceLoaded && targetLoaded && sourceTotalPages > 0 && targetTotalPages > 0;
  },
);

const getCreateInspectionData = createSelector(
  [getName, getFilesIds, getInspectionSettings],
  (name, filesIds, settings): CreateInspectionData => {
    const inspectionData: CreateInspectionData = {};
    inspectionData.name = name;
    if (filesIds.masterFileId) {
      inspectionData.masterFileId = filesIds.masterFileId;
    }
    if (filesIds.sampleFileId) {
      inspectionData.sampleFileId = filesIds.sampleFileId;
    }

    inspectionData.settings = settings;
    return inspectionData;
  },
);

const getDifferenceViewOptions = createSelector(
  [getInspectionstate],
  (inspectionState) => inspectionState.differenceViewOptions,
);

const getFocusedDifferenceId = createSelector(
  [getInspectionstate],
  (inspectionState) => inspectionState.focusedDifferenceId,
);

const getPreviousDifferenceId = createSelector(
  [getInspectionstate],
  (inspectionState) => inspectionState.previousDifferenceId,
);

const getDifferencesLoadedSelector = createSelector(
  [getInspectionstate],
  (inspectionState) => inspectionState.differencesLoaded,
);

const getInspectionStarted = createSelector(
  [getInspectionStatus],
  (status) => status === InspectionStatuses.queued || status === InspectionStatuses.inprogress,
);

const getInspectionCompleted = createSelector(
  [getInspectionStatus],
  (status) => status === InspectionStatuses.completed,
);

const getInspectionFailed = createSelector([getInspectionStatus], (status) => status === InspectionStatuses.error);

const getInspectionIdle = createSelector([getInspectionStatus], (status) => status === InspectionStatuses.idle);

const getDocumentsLoaded = createSelector(
  [getSourceLoaded, getTargetLoaded, getInspectionSettings],
  (source, target, settings) => {
    if (settings.singleFile) {
      return target;
    }
    return source && target;
  },
);

const getAnnotations = createSelector([getSourceAnnotations, getTargetAnnotations], (source, target) => ({
  source,
  target,
}));

const getCurrentPage = createSelector([getSourceCurrentPage, getTargetCurrentPage], (source, target) => ({
  source,
  target,
}));

const getPages = createSelector([getSourcePages, getTargetPages], (source, target) => ({
  source,
  target,
}));

const getDocumentsState = createSelector(
  [getSourceState, getTargetState, getReportState],
  (source, target, report) => ({
    source,
    target,
    report,
  }),
);

const getUpdateInspectionData = createSelector(
  [getInspectionInput, getFilesIds, getInspectionSettings, getSelectedCustomDictionaryIDs],
  (input, filesIds, settings, customDictionaryIDs) => ({
    input,
    status: InspectionStatuses.idle,
    masterFileId: filesIds.masterFileId || null,
    sampleFileId: filesIds.sampleFileId || null,
    settings,
    customDictionaryIDs,
  }),
);

const getInspectionHasRun = createSelector([getInspectionLastJobId], (lastJobId) => Boolean(lastJobId));

// check if the inspection has results but the markups have change
const getInspectionIsOutdated = createSelector(
  [getInspectionLastJobId, getInspectionStatus],
  (lastJobId, status) => Boolean(lastJobId) && status !== InspectionStatuses.completed,
);

const getAllPageLoadedSelector = createSelector(
  [getSourceLoaded, getTargetLoaded, getInspectionSettings, getHideDisplayPanels],
  (source, target, settings, hideDisplayPanels) => {
    if (settings.singleFile) {
      return target;
    }
    if (hideDisplayPanels.source) {
      return target;
    } else if (hideDisplayPanels.target) {
      return source;
    } else {
      return source && target;
    }
  },
);

const getNumberOfAnnotations = createSelector([getAnnotations], (annotations) => {
  const annotationCounters = {
    [PDFTronTools.GRAPHIC]: {
      source: { shifted: 0, scaled: 0, autoGraphic: 0, matchedGraphic: 0 },
    },
    [PDFTronTools.ZONE]: {
      source: 0,
    },
    [PDFTronTools.MARQUEE_ZONE]: {
      source: 0,
    },
    [PDFTronTools.CROSSOUT]: {
      source: 0,
      target: 0,
    },
    [PDFTronTools.CROP]: {
      source: 0,
      target: 0,
    },
  };

  annotations.source.forEach((annot) => {
    const { usedTool } = annot;
    if (annotationCounters) {
      if (
        usedTool === PDFTronTools.ZONE ||
        usedTool === PDFTronTools.MARQUEE_ZONE ||
        usedTool === PDFTronTools.CROSSOUT ||
        usedTool === PDFTronTools.CROP
      ) {
        annotationCounters[usedTool].source++;
      } else if (usedTool === PDFTronTools.GRAPHIC) {
        if (annot.graphicZone?.scaled) {
          annotationCounters[PDFTronTools.GRAPHIC].source.scaled++;
        } else if (annot.graphicZone?.autoGraphic) {
          annotationCounters[PDFTronTools.GRAPHIC].source.autoGraphic++;
        } else if (annot.graphicZone?.matchedGraphic) {
          annotationCounters[PDFTronTools.GRAPHIC].source.matchedGraphic++;
        } else if (annot.graphicZone?.shifted) {
          annotationCounters[PDFTronTools.GRAPHIC].source.shifted++;
        }
      }
    }
  });
  annotations.target.forEach((annot) => {
    if (annotationCounters) {
      const { usedTool } = annot;
      if (usedTool === PDFTronTools.CROSSOUT) {
        annotationCounters[PDFTronTools.CROSSOUT].target++;
      }
      if (usedTool === PDFTronTools.CROP) {
        annotationCounters[PDFTronTools.CROP].target++;
      }
    }
  });
  return annotationCounters;
});

// Keyed on the annotations arrays rather than the whole documents, which the viewer replaces on
// every setCurrentPage while the user scrolls. Keying on the documents meant this memo never hit
// and every subscriber got a fresh { alignMatch, scaleMatch, stretchMatch } object on each scroll tick.
const getManualAlignTrackingData = createSelector(
  [
    (state: StoreState) => state.inspection[DocumentTypes.source].annotations,
    (state: StoreState) => state.inspection[DocumentTypes.target].annotations,
  ],
  (sourceAnnotations, targetAnnotations) => {
    const sourceZones = getManualAlignZonesData({ annotations: sourceAnnotations });
    const targetZones = getManualAlignZonesData({ annotations: targetAnnotations });
    const allZones = [...sourceZones, ...targetZones];
    const overlayZones = allZones.filter((zone) => zone.annotationId !== zone.linkedZoneId);
    const hasOverlays = overlayZones.length > 0;
    const alignMatch = hasOverlays && overlayZones.some((zone) => !zone.resized);
    const scaleMatch = hasOverlays && overlayZones.some((zone) => zone.resized === true);
    const stretchMatch =
      hasOverlays && overlayZones.some((zone) => zone.resized === true && zone.aspectRatioUnlocked === true);
    return { alignMatch, scaleMatch, stretchMatch };
  },
);

const getSpellingTrackingData = createSelector([getInspectionSettings], (settings): string => {
  if (!settings.dictionaryName) return 'false';
  const selectedDictionary = Object.values(Dictionaries).find(
    (dictionary: string) => dictionary === settings.dictionaryName,
  );
  return `true+${selectedDictionary}`;
});

const getIsFiltering = createSelector(
  [getMutationSelector({ type: updateDifferenceViewOptions, requestKey: 'updateFilters' })],
  (inspectionMutation: MutationState) => {
    return inspectionMutation.loading;
  },
);

const getShiftedGraphicRefId = (state: StoreState) => state.inspection.shiftedGraphicRefId;

const getisBarcode = (state: StoreState) => state.inspection.isBarcode;

const getIsSpelling = (state: StoreState) => state.inspection.settings.spelling;

const getUnmatchedPages = (state: StoreState) => state.inspection.unmatchedPages;

const getUnmatchedCropPages = (state: StoreState) => state.inspection.unmatchedCropPages;

const getIsResultsPanelHidden = (state: StoreState) => state.inspection.isResultsPanelHidden;

const getGraphicsSensitivity = (state: StoreState) => state.inspection.settings.graphics?.sensitivity;

const getBarcodeProfile = (state: StoreState) => state.inspection.settings.barcodeSettings?.customProfileId;

const getHighSensitivitySelected = createSelector(
  getGraphicsSensitivity,
  (sensitivity) => sensitivity === GraphicsSensitivity.HIGH,
);

const getGraphicsMatchingMode = (state: StoreState) => state.inspection.settings.graphics?.matchingMode;

const getOneToOneSelected = createSelector(
  getGraphicsMatchingMode,
  (matchingMode) => matchingMode === MatchingMode.OneToOne,
);

const getTextMatchingMode = (state: StoreState) => state.inspection.settings.textMatchingMode;

const getTextOneToOneSelected = createSelector(
  getTextMatchingMode,
  (matchingMode) => matchingMode === MatchingMode.OneToOne,
);

// Keyed on the three fields getPagesData actually reads rather than the whole document, so that
// unrelated updates to it — currentPage above all, which the viewer dispatches continuously while
// scrolling — don't invalidate the memo and hand subscribers a new object.
const makePagesDataSelector = (documentType: DocumentTypes) =>
  createSelector(
    [
      (state: StoreState) => state.inspection[documentType].pageRange,
      (state: StoreState) => state.inspection[documentType].totalPageCount,
      (state: StoreState) => state.inspection[documentType].annotations,
    ],
    (pageRange, totalPageCount, annotations) => getPagesData({ pageRange, totalPageCount, annotations }),
  );

const getSourcePagesData = makePagesDataSelector(DocumentTypes.source);
const getTargetPagesData = makePagesDataSelector(DocumentTypes.target);

const collectCropZones = (pages: InputPage[]): Crop[] =>
  pages.reduce<Crop[]>((accum, page) => accum.concat(page.crop || []), []);

const getPagesCropZones = createSelector([getSourcePagesData, getTargetPagesData], (sourcePages, targetPages) => ({
  source: collectCropZones(sourcePages),
  target: collectCropZones(targetPages),
}));

const getDocumentPolygons = (state: StoreState, documentType: DocumentTypes) => state.inspection.polygons[documentType];

const getPolygonsAreActive = createSelector(
  [
    (state: StoreState) => getActivePolygonKey(state, DocumentTypes.source),
    (state: StoreState) => getActivePolygonKey(state, DocumentTypes.target),
  ],
  (sourceKey, targetKey) => ({
    sourcePolygonsActive: sourceKey !== '',
    targetPolygonsActive: targetKey !== '',
  }),
);

const getActivePolygonKey = (state: StoreState, documentType: DocumentTypes) =>
  state.inspection.polygons[documentType]?.key;

// Step-and-repeat list item selectors

// Selector to categorize repeats into those with and without differences
const getCategorizedRepeats = createSelector([getRepeatsPerPage], (repeatsPerPage) => {
  const repeatsWithDifferences: RepeatWithPage[] = [];
  const repeatsWithoutDifferences: RepeatWithPage[] = [];

  Object.keys(repeatsPerPage).forEach((pageNumber) => {
    const repeatSourcePageNumber = parseInt(pageNumber);
    const pageRepeats = repeatsPerPage[repeatSourcePageNumber];

    pageRepeats.forEach((repeat) => {
      // There are 2 types of repeats with 0 differences: 0 diffs detected during inspection and 0 diffs after filtering
      // Both will have different states in the UI so we keep track of them separately
      const hasNoDifferences = repeat.differences.length === 0;
      const hasNoFilteredDifferences = repeat.differencesPostFiltering.length === 0;

      // Repeats with no differences after filtering are not displayed in the list at all
      if (!hasNoDifferences && hasNoFilteredDifferences) {
        return;
      }

      const repeatWithPage = {
        repeat,
        sourcePageNumber: repeatSourcePageNumber,
      };

      if (hasNoDifferences) {
        repeatsWithoutDifferences.push(repeatWithPage);
      } else {
        repeatsWithDifferences.push(repeatWithPage);
      }
    });
  });

  return { repeatsWithDifferences, repeatsWithoutDifferences };
});

// Function to sort repeats by page number, then by match index
const sortRepeats = (repeats: RepeatWithPage[]) => {
  return [...repeats].sort((a, b) => {
    if (a.sourcePageNumber !== b.sourcePageNumber) {
      return a.sourcePageNumber - b.sourcePageNumber;
    }
    return a.repeat.index - b.repeat.index;
  });
};

// Selector to get sorted categorized repeats
const getSortedCategorizedRepeats = createSelector(
  [getCategorizedRepeats],
  ({ repeatsWithDifferences, repeatsWithoutDifferences }) => ({
    repeatsWithDifferences: sortRepeats(repeatsWithDifferences),
    repeatsWithoutDifferences: sortRepeats(repeatsWithoutDifferences),
  }),
);

// Function that returns a selector for list items given the expansion state and inspection custom statuses
const getStepAndRepeatListItems = (
  isNoDifferencesExpanded: boolean,
  inspectionCustomStatuses: InspectionCustomStatuses | undefined | null,
) =>
  createSelector(
    [getSortedCategorizedRepeats, getDifferenceSortOption],
    ({ repeatsWithDifferences, repeatsWithoutDifferences }, sortOption) =>
      buildStepAndRepeatListItems(
        repeatsWithDifferences,
        repeatsWithoutDifferences,
        sortOption,
        inspectionCustomStatuses,
        isNoDifferencesExpanded,
      ),
  );
const getInspectionOwnerId = (state: StoreState) => state.inspection.userId;

const getIsSharing = (state: StoreState) => state.inspection.settings.isSharing;

const getInspectionOriginalOrgId = (state: StoreState) => state.inspection.settings.originalOrgId;

export {
  getPageRangeSelector,
  getPageLoadedSelector,
  getLiveTextNotFound,
  getLiveTextStatus,
  getTextProcessStatus,
  getInspectionId,
  getName,
  getDifferencePanelOpen,
  getChangeGridDirection,
  getChangeGridWidth,
  getTotalPageCount,
  getDocumentDisplayedSelector,
  getDifferenceViewOptions,
  getFocusedDifferenceId,
  getDifferencesLoadedSelector,
  getInspectionStarted,
  getSearchingForGraphic,
  getZoneSelectedTexts,
  getInspectionCompleted,
  getInspectionFailed,
  getSelectedTool,
  getManualSelectedZoneId,
  getManualSelectedAnnotationId,
  getManualAlignMode,
  getDocumentsLoaded,
  getReportLoaded,
  getAnnotations,
  getCurrentPage,
  getPages,
  getSyncScrolling,
  getInvalidScaledGraphicZoneRatio,
  getUnconfirmTextZoneError,
  getUnconfirmGraphicZoneError,
  getUnconfirmedMarqueeZoneError,
  getPendingCropZone,
  getPendingShapesRequest,
  getNoResultAnnotationId,
  getPersistedDifferenceComments,
  getDocumentsState,
  getUpdateInspectionData,
  getInspectionHasRun,
  getInspectionIsOutdated,
  getCreateInspectionData,
  getSelectingZone,
  getActivePolygonCreation,
  getOpenPanel,
  getInspectionIdle,
  getIsSingleFile,
  getPagesData,
  getExcludedPage,
  getPageRangeOption,
  getShowLibrary,
  getPreviousDifferenceId,
  getInternalAnnotationVisibility,
  getInternalAnnotationsVisibilityDefault,
  getInspectionLastJobId,
  getNextTextZoneId,
  getNextGraphicZoneId,
  getNextCropZoneId,
  getNextMarqueeZoneId,
  getNextCrossoutZoneId,
  getNextColorPointId,
  getNextManualAlignZoneId,
  getHighlightColor,
  getNumberOfAnnotations,
  getManualAlignTrackingData,
  getInspectionSettings,
  getSelectedCustomDictionaryIDs,
  getTableEnhancementEnabled,
  getTargetLiveText,
  getSourceLiveText,
  getSpellingTrackingData,
  getIsFiltering,
  getLastJobInput,
  getLastJobCreatedAt,
  getShiftedGraphicRefId,
  getAutoMatchGraphic,
  getIsFilesLoaded,
  getLoadingDifferences,
  getResyncLoading,
  getProcessingResyncResults,
  getUndoingResync,
  getSourceLayers,
  getTargetLayers,
  getSourceSeparations,
  getTargetSeparations,
  getSeparationHidden,
  getisBarcode,
  getIsSpelling,
  getAllPageLoadedSelector,
  getUnmatchedPages,
  getUnmatchedCropPages,
  getEitherLiveText,
  getCtfJobs,
  getTextMatchJobId,
  getTextMatchCdnPathPrefix,
  getTextMatchMetadata,
  getLastJobMetadata,
  getLastJobCtfVersion,
  getInspectionStatus,
  getIsResultsPanelHidden,
  getSourceState,
  getTargetState,
  getGraphicsSensitivity,
  getBarcodeProfile,
  getGraphicsMatchingMode,
  getHighSensitivitySelected,
  getOneToOneSelected,
  getTextMatchingMode,
  getTextOneToOneSelected,
  getInspectionHasOutdatedGraphicZones,
  getLastJobMatches,
  getMatchDetailsByMatchIndex,
  getPagesImagesPath,
  getThumbnailsImagesPath,
  getPagesCropZones,
  getDocumentPolygons,
  getActivePolygonKey,
  getPolygonsAreActive,
  getChangeToolError,
  getIsStepAndRepeat,
  getRepeats,
  getDisplayedRepeats,
  getDisplayedAndHiddenRepeats,
  getRepeatsPerPage,
  getRepeatHasDifferences,
  getRepeatIsDisplayed,
  getCategorizedRepeats,
  getSortedCategorizedRepeats,
  getStepAndRepeatListItems,
  getLastJobImages,
  getInspectionOwnerId,
  getIsSharing,
  getSourceOverprint,
  getTargetOverprint,
  getInspectionOriginalOrgId,
  getImagesPathPrefix,
};
